import { axiosInstance } from './axiosV2Config';
import { useAuthStore, type User, type UserProfile, type Society } from '../Stores/auth-v2-store';

// Capture le authTransfer au chargement du module, AVANT que React Router ne redirige et perde le query param
const _initialUrlParams = new URLSearchParams(window.location.search);
const _capturedAuthTransfer = _initialUrlParams.get('authTransfer');

interface LoginCredentials {
  email: string;
  password: string;
  captcha?: string;
}

interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  profile?: UserProfile;
  society?: Society;
  requirePasswordChange?: boolean;
}

interface RefreshResponse {
  accessToken: string;
}

interface ProfileResponse {
  user: User;
  profile: UserProfile;
  society: Society | null;
  isImpersonating?: boolean;
  originalUser?: User;
  requirePasswordChange?: boolean;
}

interface UpdateProfileRequest {
  uiTheme?: string;
  uiLanguage?: string;
  dashboardLayout?: Record<string, any>;
  notificationPreferences?: Record<string, any>;
  monthlySalesTarget?: number;
}

interface ImpersonateResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  profile?: UserProfile;
  society?: Society | null;
  isImpersonating: boolean;
  originalUser: {
    id: string;
    type: 'EXTERNAL' | 'INTERNAL';
  };
}

interface StopImpersonateResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  profile: UserProfile;
  society: Society | null;
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<void> => {
    const { setAuth, setLoading, setError } = useAuthStore.getState();

    try {
      setLoading(true);
      setError(null);

      const { data } = await axiosInstance.post<LoginResponse>('/auth-v2/login', credentials);

      setAuth({
        accessToken: data.accessToken!,
        refreshToken: data.refreshToken!,
        user: data.user!,
        profile: data.profile,
        society: data.society,
        requirePasswordChange: data.requirePasswordChange,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erreur lors de la connexion';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  },

  refreshToken: async (refreshToken: string): Promise<RefreshResponse> => {
    const { data } = await axiosInstance.post('/auth-v2/refresh', { refreshToken });
    return data;
  },

  getProfile: async (): Promise<ProfileResponse> => {
    const { data } = await axiosInstance.get<ProfileResponse>('/auth-v2/profile');
    return data;
  },

  updateProfile: async (preferences: UpdateProfileRequest): Promise<UserProfile> => {
    const { setProfile, updateProfilePreferences } = useAuthStore.getState();

    updateProfilePreferences(preferences);

    try {
      const { data } = await axiosInstance.put<{ profile: UserProfile }>(
        '/auth-v2/profile',
        preferences
      );

      setProfile(data.profile);
      return data.profile;
    } catch (error) {
      const profileData = await authApi.getProfile();
      setProfile(profileData.profile);
      throw error;
    }
  },

  completeOnboarding: async (step?: number): Promise<void> => {
    const { setProfile } = useAuthStore.getState();

    const { data } = await axiosInstance.post<{ profile: UserProfile }>(
      '/auth-v2/complete-onboarding',
      { step }
    );

    setProfile(data.profile);
  },

  markTutorialAsSeen: async (tutorialKey: string): Promise<void> => {
    await axiosInstance.post(`/auth-v2/mark-tutorial-seen/${tutorialKey}`, {
      tutorialKey,
    });

    const profileData = await authApi.getProfile();
    useAuthStore.getState().setProfile(profileData.profile);
  },

  logout: async (): Promise<void> => {
    const { logout } = useAuthStore.getState();

    try {
      await axiosInstance.post('/auth-v2/logout');
    } catch (error) {
      console.error('Erreur lors du logout:', error);
    } finally {
      logout();
    }
  },

  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    await axiosInstance.post('/auth-v2/change-password', { oldPassword, newPassword });
  },

  forceChangePassword: async (newPassword: string): Promise<void> => {
    await axiosInstance.post('/auth-v2/force-change-password', { newPassword });
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.post('/auth-v2/forgot-password', {
      email,
      frontendUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
    });
    return data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.post('/auth-v2/reset-password', { token, newPassword });
    return data;
  },

  getLoginHistory: async (): Promise<any[]> => {
    const { data } = await axiosInstance.get('/auth-v2/login-history');
    return data.logs || [];
  },

  initializeAuth: async (): Promise<void> => {
    if (!useAuthStore.persist.hasHydrated()) {
      await new Promise<void>((resolve) => {
        useAuthStore.persist.onFinishHydration(() => resolve());
      });
    }

    // Utilise le token capturé au chargement du module (avant que React Router ne redirige et perde le query param)
    const transferToken = _capturedAuthTransfer;
    if (transferToken) {
      // Nettoyer l'URL si le param est encore présent
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('authTransfer')) {
        urlParams.delete('authTransfer');
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
      useAuthStore.getState().setTokens(transferToken, transferToken);
    }

    const { refreshToken, isImpersonating, originalUser } = useAuthStore.getState();

    if (!refreshToken) {
      useAuthStore.getState().setAuthReady(true);
      return;
    }

    const { setAuth, setLoading, setTokens, logout } = useAuthStore.getState();

    setLoading(true);

    try {
      const { data: refreshData } = await axiosInstance.post('/auth-v2/refresh', {
        refreshToken
      });

      setTokens(refreshData.accessToken, refreshToken);

      const profileData = await authApi.getProfile();

      const effectiveIsImpersonating = profileData.isImpersonating || isImpersonating;
      const effectiveOriginalUser = profileData.originalUser || originalUser || undefined;

      setAuth({
        accessToken: refreshData.accessToken,
        refreshToken,
        user: profileData.user,
        profile: profileData.profile,
        society: profileData.society || undefined,
        isImpersonating: effectiveIsImpersonating,
        originalUser: effectiveOriginalUser,
        requirePasswordChange: profileData.requirePasswordChange,
      });
    } catch (error: any) {
      console.error('Échec de l\'initialisation de l\'authentification:', error);
      const status = error?.response?.status;
      const isAuthFailure = status === 401 || status === 403;
      if (isAuthFailure) {
        logout();
      }
    } finally {
      setLoading(false);
      useAuthStore.getState().setAuthReady(true);
    }
  },

  impersonate: async (targetUserId: string): Promise<void> => {
    const { startImpersonation, setLoading, setError, currentUser } = useAuthStore.getState();

    try {
      setLoading(true);
      setError(null);

      const { data } = await axiosInstance.post<ImpersonateResponse>(
        `/auth-v2/impersonate/${targetUserId}`
      );

      startImpersonation({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
        profile: data.profile,
        society: data.society ?? undefined,
        originalUser: currentUser!,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erreur lors de l\'impersonation';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  },

  stopImpersonate: async (): Promise<void> => {
    const { stopImpersonation, setLoading, setError } = useAuthStore.getState();

    try {
      setLoading(true);
      setError(null);

      const { data } = await axiosInstance.post<StopImpersonateResponse>('/auth-v2/stop-impersonate');

      stopImpersonation({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
        profile: data.profile,
        society: data.society ?? undefined,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erreur lors de l\'arrêt de l\'impersonation';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  },
};
